import * as styles from "./PageHeader.css";

import type { JSX } from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { tagNameFor } from "@frontend/bridge/tagName";
import type { IconName } from "@frontend/components/atoms/Icon";

import { PageHeader } from "./PageHeader";
import {
  type PageHeaderActionsDataProps,
  type PageHeaderBreadcrumbDataProps,
  type PageHeaderDataProps,
  type PageHeaderPartDataProps,
  type PageHeaderVisualDataProps,
  pageHeaderActionsDataSchema,
  pageHeaderBreadcrumbDataSchema,
  pageHeaderDataSchema,
  pageHeaderPartDataSchema,
  pageHeaderVisualDataSchema,
} from "./PageHeader.schema";
import type { PageHeaderVariant } from "./PageHeader.types";
import { PageHeaderActions } from "./PageHeaderActions";
import { PageHeaderBreadcrumb } from "./PageHeaderBreadcrumb";
import { PageHeaderContent } from "./PageHeaderContent";
import { PageHeaderHeading } from "./PageHeaderHeading";
import { PageHeaderMeta } from "./PageHeaderMeta";
import { PageHeaderVisual } from "./PageHeaderVisual";
import {
  PAGE_HEADER_ACTIONS_TAG_NAME,
  PAGE_HEADER_BREADCRUMB_TAG_NAME,
  PAGE_HEADER_CONTENT_TAG_NAME,
  PAGE_HEADER_HEADING_TAG_NAME,
  PAGE_HEADER_META_TAG_NAME,
  PAGE_HEADER_TAG_NAME,
  PAGE_HEADER_VISUAL_TAG_NAME,
} from "./constants";

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [PAGE_HEADER_TAG_NAME]: { props: PageHeaderDataProps; handle: undefined };
    [PAGE_HEADER_VISUAL_TAG_NAME]: {
      props: PageHeaderVisualDataProps;
      handle: undefined;
    };
    [PAGE_HEADER_CONTENT_TAG_NAME]: {
      props: PageHeaderPartDataProps;
      handle: undefined;
    };
    [PAGE_HEADER_BREADCRUMB_TAG_NAME]: {
      props: PageHeaderBreadcrumbDataProps;
      handle: undefined;
    };
    [PAGE_HEADER_HEADING_TAG_NAME]: {
      props: PageHeaderPartDataProps;
      handle: undefined;
    };
    [PAGE_HEADER_META_TAG_NAME]: {
      props: PageHeaderPartDataProps;
      handle: undefined;
    };
    [PAGE_HEADER_ACTIONS_TAG_NAME]: {
      props: PageHeaderActionsDataProps;
      handle: undefined;
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-page-header": HTMLElement;
    "rwc-page-header-visual": HTMLElement;
    "rwc-page-header-content": HTMLElement;
    "rwc-page-header-breadcrumb": HTMLElement;
    "rwc-page-header-heading": HTMLElement;
    "rwc-page-header-meta": HTMLElement;
    "rwc-page-header-actions": HTMLElement;
  }
}

function readVariantFromHeader(el: HTMLElement): {
  variant: PageHeaderVariant;
} {
  const parent = el.closest<HTMLElement>(tagNameFor(PAGE_HEADER_TAG_NAME));

  const own = el.getAttribute("variant") as PageHeaderVariant | null;

  return {
    variant:
      own || (parent?.getAttribute("variant") as PageHeaderVariant) || "card",
  };
}

function RWCPageHeader({
  icon,
  variant = "card",
}: PageHeaderDataProps): JSX.Element {
  return (
    <PageHeader icon={icon as IconName | undefined} variant={variant}>
      <slot />
    </PageHeader>
  );
}

function RWCPageHeaderVisual({
  icon,
  variant = "card",
}: PageHeaderVisualDataProps & { variant?: PageHeaderVariant }): JSX.Element {
  return (
    <PageHeaderVisual icon={icon as IconName | undefined} variant={variant}>
      {!icon && <slot className={styles.visualSlot} />}
    </PageHeaderVisual>
  );
}

function RWCPageHeaderContent(): JSX.Element {
  return (
    <PageHeaderContent>
      <slot />
    </PageHeaderContent>
  );
}

function RWCPageHeaderBreadcrumb({
  icon,
}: PageHeaderBreadcrumbDataProps): JSX.Element {
  return (
    <PageHeaderBreadcrumb icon={icon as IconName | undefined}>
      <slot />
    </PageHeaderBreadcrumb>
  );
}

function RWCPageHeaderHeading(): JSX.Element {
  return (
    <PageHeaderHeading>
      <slot className={styles.headingSlot} />
    </PageHeaderHeading>
  );
}

function RWCPageHeaderMeta(): JSX.Element {
  return (
    <PageHeaderMeta>
      <slot className={styles.metaSlot} />
    </PageHeaderMeta>
  );
}

function RWCPageHeaderActions({
  menuLabel,
  variant = "card",
}: PageHeaderActionsDataProps & {
  variant?: PageHeaderVariant;
}): JSX.Element {
  return (
    <PageHeaderActions menuLabel={menuLabel}>
      <slot className={styles.actionsSlot({ variant })} />
    </PageHeaderActions>
  );
}

componentManager.register(
  rwc(PAGE_HEADER_TAG_NAME, {
    component: RWCPageHeader,
    dataSchema: pageHeaderDataSchema,
    usesSlots: true,
  }),
  rwc(PAGE_HEADER_VISUAL_TAG_NAME, {
    component: RWCPageHeaderVisual,
    dataSchema: pageHeaderVisualDataSchema,
    usesSlots: true,
    readSlotted: readVariantFromHeader,
  }),
  rwc(PAGE_HEADER_CONTENT_TAG_NAME, {
    component: RWCPageHeaderContent,
    dataSchema: pageHeaderPartDataSchema,
    usesSlots: true,
  }),
  rwc(PAGE_HEADER_BREADCRUMB_TAG_NAME, {
    component: RWCPageHeaderBreadcrumb,
    dataSchema: pageHeaderBreadcrumbDataSchema,
    usesSlots: true,
  }),
  rwc(PAGE_HEADER_HEADING_TAG_NAME, {
    component: RWCPageHeaderHeading,
    dataSchema: pageHeaderPartDataSchema,
    usesSlots: true,
  }),
  rwc(PAGE_HEADER_META_TAG_NAME, {
    component: RWCPageHeaderMeta,
    dataSchema: pageHeaderPartDataSchema,
    usesSlots: true,
  }),
  rwc(PAGE_HEADER_ACTIONS_TAG_NAME, {
    component: RWCPageHeaderActions,
    dataSchema: pageHeaderActionsDataSchema,
    usesSlots: true,
    readSlotted: readVariantFromHeader,
  }),
);
